import { Pressable, SxProp, View } from 'dripsy';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrenciesContext } from '@/currencies/currency.context';
import { EarnStackRoutes } from '@/features/earn';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { Icon } from '../icons/Icon';
import { UniInput } from '../inputs';
import { TextBody, TextCaption, TextSmall } from '../typography';
import { AccountSelectorDropdownMenu } from './AccountSelectorDropdownMenu';
import { useAccountSelector, WalletData } from './useAccountSelector';

interface AccountSelectorProps {
  asset?: string;
  value?: string;
  type?: string;
  sx?: SxProp;
  showBalance?: boolean;
  checkBalance?: boolean;
  disabledForwardBrowser?: boolean; // flag to disable forward browser button after pressed go back navigation
  onValueChange: (value: string) => void;
  onWalletChange?: (value: WalletData | undefined) => void;
  dropdownSx?: SxProp;
}

export const AccountSelector: React.FC<AccountSelectorProps> = ({
  asset,
  value,
  type,
  sx,
  showBalance,
  checkBalance,
  disabledForwardBrowser,
  onValueChange,
  onWalletChange,
  dropdownSx
}) => {
  const { t } = useTranslation('earn');
  const navigation = useNavigation<any>();
  const layout = useContext(LayoutContext);
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  const {
    hasFocus,
    selectedWallet,
    selectedWalletId,
    displayWalletData,
    changeFocus,
    changeSearchText,
    selectWalletId,
    getDecimalsByAsset
  } = useAccountSelector({
    asset,
    value,
    type,
    checkBalance,
    onValueChange,
    onWalletChange
  });

  return (
    <View sx={{ ...styles.container, ...sx }}>
      {/* Button */}
      <Pressable
        onPress={() => {
          if (layout.isMobile) {
            navigation.push(EarnStackRoutes.AccountSelect, {
              asset,
              value: selectedWalletId,
              disabledForwardBrowser,
              onSelect: selectWalletId
            });
            return;
          }
          changeFocus(true);
        }}>
        {hasFocus ? (
          <UniInput
            hasFocus={hasFocus}
            prefixIcon="search"
            onValueChange={(value) => changeSearchText(value || '')}
            placeholder={t('earn-invest::glossary.search-account')}
            wrapperSx={{ flexGrow: 1 }}
            sx={{ width: '100%', height: '$15' }}
          />
        ) : null}
        {!hasFocus && !selectedWalletId ? (
          <View sx={styles.accountButton}>
            <TextBody sx={{ color: '$textPlaceholder' }}>{t('earn-invest::glossary.select-account')}</TextBody>
            <Icon name={layout.isMobile ? 'arrowRight' : 'arrowDown'} />
          </View>
        ) : null}
        {!hasFocus && selectedWalletId ? (
          <View sx={styles.accountButton}>
            <View>
              <TextBody label>{selectedWallet?.label}</TextBody>
              <TextSmall secondary>{selectedWallet?.accountNumber}</TextSmall>
            </View>
            <Icon name={layout.isMobile ? 'arrowRight' : 'arrowDown'} />
          </View>
        ) : null}
        {selectedWallet && showBalance && (
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', mt: '$1' }}>
            <TextCaption>{t('earn-invest::glossary.balance')}</TextCaption>
            <TextCaption>
              {formatAmountByDecimals({
                value: selectedWallet.balance,
                decimals: getDecimalByAsset(selectedWallet.asset),
                suffix: selectedWallet.asset
              })}
            </TextCaption>
          </View>
        )}
      </Pressable>
      {/* Dropdown Menu */}
      <AccountSelectorDropdownMenu
        hasFocus={hasFocus}
        displayWalletData={displayWalletData}
        changeFocus={changeFocus}
        getDecimalsByAsset={getDecimalsByAsset}
        selectWalletId={selectWalletId}
        onValueChange={onValueChange}
        dropdownSx={dropdownSx}
      />
    </View>
  );
};

enum StyleName {
  container = 'container',
  accountButton = 'accountButton',
  dropdownMenu = 'dropdownMenu',
  dropdownItem = 'dropdownItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    mb: '$5',
    zIndex: 10
  },
  accountButton: {
    height: '$15',
    paddingX: '$4',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '$borderField',
    borderRadius: 5
  },
  dropdownMenu: {
    position: 'absolute',
    top: '$15',
    left: 0,
    width: '100%',
    maxHeight: 250,
    paddingY: '$2',
    backgroundColor: '$backgroundPopup',
    borderRadius: 5,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10
  },
  dropdownItem: {
    paddingX: '$3',
    paddingY: '$4',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  }
};
